import type { RenderedMessage } from '@/api/Rover/notifications/roverNotifications.types';
import { cx } from '../ui/primitives';

/** Visual previews for each channel. Shared by the preview modal and the status-change modal. */

export function EmailPreview({
  message,
  from,
  to,
  programName,
  width = 'desktop',
}: {
  message: RenderedMessage;
  from: string;
  to: string;
  programName: string;
  width?: 'desktop' | 'mobile';
}) {
  return (
    <div className='flex flex-col'>
      <dl className='grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 border-b border-gray-200 bg-gray-50 px-6 py-3 text-sm text-gray-700'>
        <dt className='font-semibold'>From:</dt>
        <dd>{from}</dd>
        <dt className='font-semibold'>To:</dt>
        <dd>{to}</dd>
        <dt className='font-semibold'>Subject:</dt>
        <dd>{message.subject}</dd>
      </dl>
      <div className='flex justify-center bg-gray-100 p-6'>
        <div className={cx('overflow-hidden rounded-lg bg-white shadow-sm', width === 'mobile' ? 'w-[375px]' : 'w-full max-w-[640px]')}>
          <div className='flex items-center gap-3 bg-emerald-800 px-6 py-4 text-white'>
            <span className='text-sm font-semibold'>{programName}</span>
          </div>
          <div className='whitespace-pre-line px-6 py-6 text-[15px] leading-relaxed text-gray-900'>{message.body}</div>
          <div className='border-t border-gray-200 px-6 py-4 text-xs text-gray-600'>
            You're receiving this email because you applied to the {programName}.
          </div>
        </div>
      </div>
    </div>
  );
}

export function SmsPreview({ message, to, from }: { message: RenderedMessage; to: string; from: string }) {
  const sms = message.sms;
  return (
    <div className='flex flex-col'>
      <div className='flex justify-between border-b border-gray-200 bg-gray-50 px-6 py-3 text-sm text-gray-700'>
        <span>
          <span className='font-semibold'>To:</span> {to} · <span className='font-semibold'>From:</span> {from}
        </span>
        {sms && (
          <span className='font-semibold'>
            {sms.characters} characters · {sms.segments} {sms.segments === 1 ? 'text' : 'texts'}
            {sms.encoding === 'UCS-2' && ' (special characters)'}
          </span>
        )}
      </div>
      <div className='flex justify-center bg-gray-100 p-6'>
        <div className='flex h-[560px] w-[340px] flex-col overflow-hidden rounded-[40px] border-[10px] border-gray-900 bg-white'>
          <div className='border-b border-gray-200 py-4 text-center text-sm font-semibold'>{from}</div>
          <div className='flex flex-1 flex-col gap-2 p-4'>
            <span className='text-center text-xs text-gray-500'>Text Message · Today</span>
            <div className='max-w-[250px] self-start rounded-2xl rounded-bl-sm bg-gray-200 px-4 py-2.5 text-sm leading-snug text-gray-900'>
              {message.body}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function OnscreenPreview({
  message,
  statusLabel,
  width = 'desktop',
}: {
  message: RenderedMessage;
  statusLabel: string;
  width?: 'desktop' | 'mobile';
}) {
  return (
    <div className='flex flex-col'>
      <div className='border-b border-gray-200 bg-gray-50 px-6 py-3 text-sm text-gray-700'>
        <span className='font-semibold'>Where:</span> Applicant status page, after the status changes
      </div>
      <div className='flex justify-center bg-gray-100 p-6'>
        <div className={cx('overflow-hidden rounded-lg bg-white shadow-sm', width === 'mobile' ? 'w-[375px]' : 'w-full max-w-[640px]')}>
          <div className='border-b border-gray-200 px-6 py-4 text-sm font-semibold'>My Application</div>
          <div className='flex flex-col gap-3 px-6 py-6'>
            <span className='self-start rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-800'>{statusLabel}</span>
            <h3 className='text-xl font-semibold text-gray-900'>{message.title}</h3>
            <div className='whitespace-pre-line text-[15px] leading-relaxed text-gray-900'>{message.body}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
